import React, { useState } from 'react';
import { Shield, Sword, Cpu, Trophy, ArrowLeft, Github, Scroll, BookOpen, FlaskConical, Target, Award, ExternalLink, Gamepad, Crosshair } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

// Import images
import bxragnar from '../assets/images/bxragnar.jpg';
import Tuner from '../assets/images/Tuner.png';
import Replio from '../assets/images/replio.png';
import AiAssistant from "../assets/images/ai-assistant.png";
import bgBatman from '../assets/images/bg-batman-v3.png';
import bgMk from '../assets/images/bg-mk.png';
import bgFortnite from '../assets/images/bg-fortnite.png';
import bgAnime from '../assets/images/bg-anime.png';
import bgMysterious from '../assets/images/bg-mysterious.png';
import ChatGPTProTools from '../assets/images/chatgptprotools.png';

const AlterEgoSection = () => {
    const { toggleAlterEgo } = useTheme();
    const [activeTab, setActiveTab] = useState('stats'); // stats, quests (projects)

    const stats = [
        { label: 'Agility (React/Frontend)', value: 95, color: 'bg-blue-500' },
        { label: 'Strength (Backend/Python)', value: 92, color: 'bg-purple-500' },
         { label: 'Intelligence (AI/LLM Architecture)', value: 90, color: 'bg-cyan-400' },
        { label: 'Mana (Cloud/DevOps)', value: 85, color: 'bg-green-400' },
    ];

    const projects = [
        {
          id: "chatgptprotools",
          title: "ChatGPT Pro Tools",
          class: "Chrome Extension",
          description: "A premium Chrome extension that enhances your AI chat experience (ChatGPT, Claude, Gemini) with powerful productivity features and beautiful themes.",
          loot: ["Astro", "TailwindCSS"],
          imageUrl: ChatGPTProTools,
          github: "https://github.com/bhimamalbhage/chatgpt-pro-tools",
          demo: "http://www.chatgptprotool.com/",
        },
        {
            id: "Replio",
            title: "Replio - AI Email Assistant",
            class: "AI Agent",
            description: "Replio is an intelligent email assistant application designed to streamline your email management using AI.",
            loot: ["Python", "React", "FastAPI"],
            imageUrl: Replio,
            github: "https://github.com/bhimamalbhage/digital-twin",
            demo: "https://replio-eight.vercel.app/",
        },
        {
            id: "tuner",
            title: "Tuner - Robotics Fine-tuning",
            class: "Robotics Platform",
            description: "Democratizing advanced robotics by enabling anyone to fine-tune general-purpose robots for real-world tasks.",
            loot: ["Python", "React", "Typescript"],
            imageUrl: Tuner,
            github: "https://github.com/clemens-chr/tuner",
        },
        {
            id: "advisor",
            title: "University AI Advisor",
            class: "EdTech AI",
            description: "A custom AI-powered advisor designed to assist university students, faculty, and staff. Won Hackathon.",
            loot: ["AI", "RAG", "Python", "React"],
            imageUrl: AiAssistant,
            github: "https://github.com/bhimamalbhage/AI-Assistant-for-university",
        },
        {
            id: "arb-litt-up",
            title: "Arb Litt Up",
            class: "Legal Tech",
            description: "LLM-powered legal argument analysis tool. Won Hackathon @Stanford.",
            loot: ["LLM", "Python", "RAG"],
            imageUrl: "https://placehold.co/600x400/1a1a1a/ef4444?text=Arb+Litt+Up",
            github: "https://github.com/asafdari-boop/arbpro",
        }
    ];

    const campaignData = [
        {
            company: "Ease Vertical AI",
            role: "Full Stack AI Product Engineer",
            period: "May 2025 - Sep 2025",
            faction: "The Vanguard",
            summary: "Led system architecture of AI-native B2B SaaS platform using multi-agent orchestration.",
            quests: [
                "Architected AI-native B2B SaaS platform using multi-agent orchestration & LLMs.",
                "Built robust backend with Python (FastAPI), LangChain, and vector databases.",
                "Engineered responsive dashboard using React.js, TypeScript, and TailwindCSS.",
                "Deployed microservices on AWS ECS + Docker, optimized with Redis & CloudWatch."
            ]
        },
        {
            company: "Deloitte",
            role: "Front End Engineer",
            period: "Apr 2021 - Aug 2023",
            faction: "The Enterprise",
            summary: "Developed large-scale enterprise applications for 300K+ users.",
            quests: [
                "Developed 15+ React/Redux UIs, achieving 95% WCAG accessibility.",
                "Mentored 4+ juniors in React ecosystem and led code reviews.",
                "Implemented Jest unit testing (95% coverage), reducing bugs by 40%."
            ]
        },
        {
            company: "Incquet Solutions",
            role: "Full Stack Engineer",
            period: "Jan 2020 - Apr 2021",
            faction: "The Builders",
            summary: "Led development of scalable DMS and AI-powered products.",
            quests: [
                "Led team of 4 devs, managing production deliveries and CI/CD pipelines.",
                "Built AI products from 0-1 using React.js, Python, and LLM integration.",
                "Developed scalable Document Management Systems with React, Node.js, Firebase."
            ]
        }
    ];

    const abilityData = {
        "Sorcery (Languages)": ["Python", "TypeScript", "JavaScript", "PHP", "SQL"],
        "Combat Styles (Frontend)": ["React", "Next.js", "Vue.js", "Redux", "Tailwind CSS", "D3.js", "Figma"],
        "Alchemy (Backend/AI)": ["FastAPI", "Node.js", "Express.js", "LangChain", "RAG Pipelines", "LLM Orchestration"],
        "Tools of War (DevOps)": ["AWS", "Docker", "Kubernetes", "GitHub Actions", "Firebase", "Jest"]
    };

    const archivesData = [
        {
            title: "Masters in Computer Science",
            institution: "Troy University",
            year: "Aug 2023 - Dec 2024",
            type: "Education"
        },
        {
            title: "Bachelors in Computer Science",
            institution: "Smt. Kashibai Nawale College Of Engineering",
            year: "Aug 2016 - Sep 2020",
            type: "Education"
        },
        {
            title: "AWS Certified Developer – Associate",
            institution: "Amazon Web Services",
            year: "Active",
            type: "Certification"
        },
        {
            title: "SPOT Excellence Award (x2)",
            institution: "Deloitte",
            year: "2022, 2023",
            type: "Achievement"
        }
    ];

    const bgImages = {
        stats: bgBatman,
        quests: bgMk,
        campaign: bgFortnite,
        abilities: bgAnime,
        archives: bgMysterious
    };

    // Animation Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 300, damping: 24 }
        }
    };

    // Particles logic (keeping the static logic but wrapping in a memo if needed, or just static array)
    const particles = [...Array(20)].map((_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100 + 100}%`,
        width: `${Math.random() * 3 + 1}px`,
        height: `${Math.random() * 3 + 1}px`,
        duration: `${Math.random() * 10 + 10}s`,
        delay: `${Math.random() * 5}s`
    }));

    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen w-full font-sans selection:bg-blue-500/30 overflow-y-auto custom-scrollbar relative perspective-1000"
        >
            {/* Dynamic Background Layer with Smooth Transition */}
            <div className="fixed inset-0 z-0">
                {Object.entries(bgImages).map(([key, img]) => (
                    <div 
                        key={key}
                        className={`absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed transition-opacity duration-500 ease-in-out ${
                            activeTab === key ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={{ backgroundImage: `url(${img})` }}
                    />
                ))}
            </div>

            {/* Ambient Background layers */}
            <div className="fixed inset-0 bg-slate-950/60 z-0 backdrop-blur-[0px]" />
            <div className="fixed inset-0 bg-grid-moving opacity-10 pointer-events-none z-0 transform-style-3d" />
            
            {/* Digital Snow / Particles */}
            <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                {particles.map((p) => (
                    <div 
                        key={p.id}
                        className="particle"
                        style={{
                            left: p.left,
                            top: p.top,
                            width: p.width,
                            height: p.height,
                            animationDuration: p.duration,
                            animationDelay: p.delay
                        }}
                    />
                ))}
            </div>

            {/* CRT Scanline Overlay */}
            <div className="fixed inset-0 pointer-events-none z-50 scanline opacity-30" />
            
            {/* Vignette */}
            <div className="fixed inset-0 pointer-events-none z-10 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.8)_100%)]" />

            {/* Top Bar / HUD - Enhanced */}
            <motion.div 
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative z-20 p-6 flex justify-between items-center border-b-2 border-cyan-500/50 bg-gradient-to-r from-slate-900/90 via-slate-800/90 to-slate-900/90 backdrop-blur-md sticky top-0 shadow-[0_0_40px_rgba(6,182,212,0.3)]"
            >
                {/* Animated border glow */}
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-pulse"></div>
                
                <div className="flex items-center gap-4">
                    <button 
                        onClick={toggleAlterEgo}
                        className="group flex items-center gap-2 px-5 py-3 border-2 border-cyan-500/70 hover:bg-cyan-500/30 text-cyan-300 font-bold uppercase tracking-widest transition-all hover:shadow-[0_0_25px_rgba(6,182,212,0.7)] relative overflow-hidden rounded"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-blue-500/20 to-cyan-400/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform relative z-10" />
                        <span className="font-mono relative z-10">Return to Base</span>
                    </button>
                    <div className="h-10 w-[2px] bg-gradient-to-b from-transparent via-cyan-500 to-transparent" />
                    <motion.h1 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="text-4xl md:text-5xl font-black tracking-[0.3em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-400 to-cyan-300 drop-shadow-[0_0_20px_rgba(6,182,212,1)] glitch-hover cursor-default relative" 
                        data-text="BXRAGNAR"
                    >
                        <span className="relative">
                            BxRagnar
                            {/* Extra glow layers */}
                            <span className="absolute inset-0 blur-xl bg-gradient-to-r from-cyan-400 to-blue-500 opacity-50 animate-pulse"></span>
                        </span>
                    </motion.h1>
                </div>
                <div className="flex gap-4 text-xs md:text-sm font-mono text-cyan-300/90 bg-slate-950/50 px-4 py-2 rounded border border-cyan-500/30">
                    <span className="animate-pulse flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-400 rounded-full shadow-[0_0_10px_rgba(74,222,128,1)]"></span>
                        ONLINE
                    </span>
                    <span className="hidden md:inline">Lvl. 25</span>
                    <span className="hidden md:inline">EXP: 84%</span>
                    <span className="hidden md:inline">Server: US-West</span>
                </div>
            </motion.div>

            <div className="max-w-7xl mx-auto p-4 md:p-12 relative z-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                
                {/* Character Profile Column */}
                <motion.div 
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="lg:col-span-4 flex flex-col gap-6"
                >
                    <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="relative group perspective-1000"
                    >
                        {/* Enhanced Frame with stronger glow */}
                        <div className="absolute -inset-2 bg-gradient-to-b from-cyan-400 via-blue-500 to-cyan-600 opacity-90 blur-xl transition duration-1000 group-hover:duration-200 group-hover:opacity-100 animate-pulse" />
                        <div className="relative aspect-[3/4] overflow-hidden border-4 border-cyan-400/70 bg-slate-800 shadow-[0_0_50px_rgba(6,182,212,0.5)] group-hover:shadow-[0_0_80px_rgba(6,182,212,0.8)] transition-all duration-500">
                             <motion.img 
                                src={bxragnar} 
                                alt="BxRagnar Character" 
                                className="w-full h-full object-cover object-top"
                                whileHover={{ scale: 1.1 }}
                                transition={{ duration: 0.7 }}
                            />
                            {/* Overlay UI elements on image */}
                            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-slate-900 via-slate-900/95 to-transparent">
                                <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-tighter text-white drop-shadow-[0_4px_0_rgba(0,0,0,1)] mb-2">
                                    Frostblade
                                </h2>
                                <p className="text-cyan-300 font-mono text-sm md:text-base flex items-center gap-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                                    <Shield className="w-5 h-5" /> Class: Code Sorcerer
                                </p>
                            </div>
                            {/* Corner accents */}
                            <div className="absolute top-2 left-2 w-6 h-6 border-t-4 border-l-4 border-cyan-400 opacity-80"></div>
                            <div className="absolute top-2 right-2 w-6 h-6 border-t-4 border-r-4 border-cyan-400 opacity-80"></div>
                            <div className="absolute bottom-2 left-2 w-6 h-6 border-b-4 border-l-4 border-cyan-400 opacity-80"></div>
                            <div className="absolute bottom-2 right-2 w-6 h-6 border-b-4 border-r-4 border-cyan-400 opacity-80"></div>
                        </div>
                    </motion.div>

                    {/* Stats Panel */}
                    <div className="bg-slate-800/40 border border-cyan-500/30 p-6 backdrop-blur-sm relative overflow-hidden group hover:border-cyan-500/60 transition-colors">
                        {/* Decorative Corners */}
                        <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-cyan-500" />
                        <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-cyan-500" />
                        
                        <h3 className="text-xl font-bold uppercase mb-6 flex items-center gap-2 text-cyan-100">
                            <Cpu className="w-5 h-5 text-cyan-400 animate-pulse" /> System Stats
                        </h3>
                        <div className="space-y-5">
                            {stats.map((stat, i) => (
                                <div key={stat.label}>
                                    <div className="flex justify-between text-xs font-mono font-bold mb-1 text-cyan-200/80">
                                        <span>{stat.label}</span>
                                        <span>{stat.value}/100</span>
                                    </div>
                                    <div className="h-2 w-full bg-slate-700/50 skew-x-[-15deg] overflow-hidden backdrop-blur-sm border border-white/5">
                                        <motion.div 
                                            className={`h-full ${stat.color} shadow-[0_0_15px_currentColor]`}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${stat.value}%` }}
                                            transition={{ duration: 1, delay: 0.5 + (i * 0.1), ease: "easeOut" }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Main Content / Quest Log */}
                <motion.div 
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.5 }}
                    className="lg:col-span-8 flex flex-col gap-8"
                >
                    
                    {/* Navigation Tabs */}
                    <div className="flex gap-4 border-b-2 border-slate-700/50 backdrop-blur-sm overflow-x-auto">
                         {['stats', 'quests', 'campaign', 'abilities', 'archives'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 md:px-8 py-3 font-bold uppercase tracking-widest text-xs md:text-sm transition-all relative whitespace-nowrap ${
                                    activeTab === tab 
                                    ? 'text-red-500 border-b-4 border-red-500 -mb-[2px] bg-red-950/30' 
                                    : 'text-slate-500 hover:text-red-300 hover:bg-white/5'
                                }`}
                            >
                                {tab === 'stats' ? 'Overview' : tab}
                            </button>
                         ))}
                    </div>

                    <div className="min-h-[400px]">
                        <AnimatePresence mode="wait">
                            {activeTab === 'stats' && (
                                <motion.div 
                                    key="stats"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    className="space-y-6"
                                >
                                    <motion.div variants={itemVariants} className="bg-slate-800/40 border-l-4 border-cyan-400 p-8 relative backdrop-blur-sm">
                                        <div className="absolute top-0 right-0 p-2 opacity-20">
                                            <Cpu className="w-24 h-24 text-cyan-500" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                                            <span className="text-cyan-400">#</span> Character Bio
                                        </h3>
                                        <p className="text-slate-300 leading-relaxed font-mono relative z-10 text-lg">
                                            Operator 'BxRagnar' is an elite Full Stack Vanguard specializing in AI-native architecture and high-scalability constructs. 
                                            Engineered for complex multi-agent orchestration and React-based interface synthesis. 
                                            Proven effectiveness in deployment across AWS combat zones and neural network integration. 
                                            Currently grinding competitive ranks in standard Earth-realm FPS simulations.
                                        </p>
                                    </motion.div>

                                    <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {[
                                            { icon: Trophy, color: 'green', title: 'Stanford Hackathon', sub: 'Status: Champion' },
                                            { icon: Award, color: 'yellow', title: 'Troy Hackathon', sub: 'Status: Winner' },
                                            { icon: Sword, color: 'red', title: 'Mortal Kombat', sub: 'Rank: Grandmaster' },
                                            { icon: Target, color: 'purple', title: 'Fortnite', sub: 'Rank: Unreal' },
                                            { icon: Crosshair, color: 'amber', title: 'Call of Duty', sub: 'Rank: Prestige Master' },
                                            { icon: Gamepad, color: 'blue', title: 'FIFA', sub: 'Rank: Elite Division' }
                                        ].map((item, idx) => (
                                            <motion.div 
                                                key={idx}
                                                variants={itemVariants}
                                                whileHover={{ scale: 1.03, backgroundColor: 'rgba(30, 41, 59, 0.8)' }}
                                                className="p-6 border border-slate-700 bg-slate-900/50 flex items-center gap-6 hover:border-cyan-500/50 transition-all cursor-default group"
                                            >
                                                <div className={`p-4 bg-${item.color}-900/20 text-${item.color}-500 rounded border border-${item.color}-500/20 group-hover:scale-110 transition-transform group-hover:shadow-[0_0_15px_currentColor]`}>
                                                    <item.icon className="w-8 h-8" />
                                                </div>
                                                <div>
                                                    <div className="font-bold text-xl text-slate-200 group-hover:text-white transition-colors">{item.title}</div>
                                                    <div className={`text-xs text-${item.color}-400 font-mono mt-1 uppercase tracking-wider`}>{item.sub}</div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                </motion.div>
                            )}

                            {activeTab === 'campaign' && (
                                <motion.div 
                                    key="campaign"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    className="space-y-8"
                                >
                                     {campaignData.map((job, index) => (
                                        <motion.div 
                                            key={index}
                                            variants={itemVariants} 
                                            className="relative pl-8 border-l-2 border-slate-700 group"
                                        >
                                            {/* Timeline Node */}
                                            <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-slate-900 border-2 border-slate-600 group-hover:border-red-500 group-hover:bg-red-900 transition-colors" />
                                            
                                            <motion.div 
                                                whileHover={{ x: 5 }}
                                                className="bg-slate-900/60 p-6 border border-slate-700/50 backdrop-blur-md relative overflow-hidden transition-all hover:border-red-500/30"
                                            >
                                                <div className="flex flex-col md:flex-row justify-between items-start mb-4">
                                                    <div>
                                                        <h3 className="text-xl font-bold text-red-50 uppercase tracking-wide">{job.role}</h3>
                                                        <div className="text-red-400 font-mono text-sm font-bold flex items-center gap-2 mt-1">
                                                            <Target className="w-4 h-4" /> @ {job.company}
                                                        </div>
                                                    </div>
                                                    <span className="text-xs font-mono text-slate-400 border border-slate-700 px-2 py-1 rounded bg-slate-950/50 mt-2 md:mt-0">
                                                        {job.period}
                                                    </span>
                                                </div>
                                                
                                                <p className="text-slate-300 italic mb-4 text-sm border-l-2 border-red-500/50 pl-3">
                                                    "{job.summary}"
                                                </p>

                                                <ul className="space-y-2">
                                                    {job.quests.map((quest, qIdx) => (
                                                        <li key={qIdx} className="text-sm text-slate-400 flex items-start gap-2 group-hover:text-slate-300 transition-colors">
                                                            <span className="text-red-500 mt-1">▸</span>
                                                            {quest}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </motion.div>
                                        </motion.div>
                                     ))}
                                </motion.div>
                            )}

                            {activeTab === 'abilities' && (
                                <motion.div 
                                    key="abilities"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    className="grid grid-cols-1 md:grid-cols-2 gap-6"
                                >
                                    {Object.entries(abilityData).map(([category, skills]) => (
                                        <motion.div 
                                            key={category} 
                                            variants={itemVariants}
                                            whileHover={{ scale: 1.02 }}
                                            className="bg-slate-900/60 p-6 border border-slate-700/50 backdrop-blur-md hover:border-red-500/30 transition-all group"
                                        >
                                            <h3 className="text-lg font-bold text-red-100 uppercase mb-4 flex items-center gap-2 border-b border-white/10 pb-2">
                                                <FlaskConical className="w-5 h-5 text-red-500" /> {category}
                                            </h3>
                                            <div className="flex flex-wrap gap-2">
                                                {skills.map(skill => (
                                                    <motion.span 
                                                        key={skill} 
                                                        whileHover={{ scale: 1.1, backgroundColor: 'rgba(220, 38, 38, 0.2)' }}
                                                        className="px-3 py-1 bg-slate-800 border border-slate-600 text-xs text-slate-300 font-mono transition-all cursor-crosshair"
                                                    >
                                                        {skill}
                                                    </motion.span>
                                                ))}
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}

                            {activeTab === 'archives' && (
                                <motion.div 
                                    key="archives"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    className="grid grid-cols-1 gap-4"
                                >
                                    {archivesData.map((item, idx) => (
                                        <motion.div 
                                            key={idx}
                                            variants={itemVariants}
                                            whileHover={{ x: 10, backgroundColor: 'rgba(30, 41, 59, 0.7)' }}
                                            className="flex items-center gap-4 bg-slate-900/60 p-4 border border-slate-700/50 backdrop-blur-md transition-colors group"
                                        >
                                            <div className="p-3 bg-slate-950 border border-slate-700 rounded text-amber-500 group-hover:text-amber-400 group-hover:border-amber-500/50 transition-all">
                                                {item.type === 'Education' ? <BookOpen className="w-6 h-6" /> : <Award className="w-6 h-6" />}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-bold text-slate-200 text-lg group-hover:text-white transition-colors">{item.title}</h4>
                                                <p className="text-slate-400 text-sm">{item.institution}</p>
                                            </div>
                                            <div className="text-xs font-mono text-slate-500 border border-slate-800 px-2 py-1 rounded">
                                                {item.year}
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}

                            {activeTab === 'quests' && (
                                <motion.div 
                                    key="quests"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    className="grid grid-cols-1 gap-6"
                                >
                                    {projects.map((project) => (
                                        <motion.div 
                                            key={project.id} 
                                            variants={itemVariants}
                                            className="relative group border border-slate-700 bg-slate-900/40 backdrop-blur-md p-1 transition-all holo-card"
                                        >
                                            <div className="flex flex-col md:flex-row gap-6 p-5 relative z-10">
                                                <div className="w-full md:w-56 aspect-video bg-black relative border border-slate-600 group-hover:border-red-400/50 transition-colors overflow-hidden">
                                                    <motion.div 
                                                        className="absolute inset-0 bg-red-500/20 z-10"
                                                        initial={{ opacity: 0 }}
                                                        whileHover={{ opacity: 1 }}
                                                    />
                                                    <motion.img 
                                                        src={project.imageUrl} 
                                                        alt={project.title} 
                                                        className="w-full h-full object-cover opacity-80"
                                                        whileHover={{ scale: 1.1, opacity: 1 }}
                                                        transition={{ duration: 0.5 }}
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div>
                                                            <h4 className="text-2xl font-black text-red-100 group-hover:text-red-400 transition-colors uppercase italic">{project.title}</h4>
                                                            <span className="text-xs font-mono text-red-600 uppercase tracking-widest border border-red-900/50 px-2 py-0.5 rounded bg-red-950/30 flex items-center gap-2 w-fit mt-1">
                                                                <Scroll className="w-3 h-3" /> {project.class}
                                                            </span>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            {project.demo && (
                                                                <a href={project.demo} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-red-400 transition-colors p-2 hover:bg-red-950/30 rounded-full">
                                                                    <ExternalLink size={20} />
                                                                </a>
                                                            )}
                                                            <a href={project.github} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-red-400 transition-colors p-2 hover:bg-red-950/30 rounded-full">
                                                                <Github className="w-6 h-6" />
                                                            </a>
                                                        </div>
                                                    </div>
                                                    <p className="text-slate-300 text-sm mb-6 border-l-2 border-slate-700 pl-4 py-1 leading-relaxed group-hover:border-red-500/50 transition-colors">
                                                        {project.description}
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {project.loot.map(tech => (
                                                            <span key={tech} className="px-3 py-1 text-[10px] uppercase font-bold bg-red-950/50 text-red-300 border border-red-800/50 rounded-sm shadow-[0_0_10px_rgba(220,38,38,0.1)]">
                                                                {tech}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Corner Accents */}
                                            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-slate-600 group-hover:border-red-400 transition-colors duration-300" />
                                            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-slate-600 group-hover:border-red-400 transition-colors duration-300" />
                                            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-slate-600 group-hover:border-red-400 transition-colors duration-300" />
                                            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-slate-600 group-hover:border-red-400 transition-colors duration-300" />
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
            
            {/* Ambient Particles/Grid (CSS handles this, just structure here) */}
            <div className="fixed bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-cyan-900/20 to-transparent pointer-events-none z-0" />
        </motion.div>
    );
};

export default AlterEgoSection;
